import * as Realm from 'realm-web'
import { ObjectId } from 'bson'

import { getCachedResponse, cacheResponse } from '../utils/storageCache'

import axios from 'axios'

let app
let client
let user

export function init() {
  if (app) {
    throw new Error('Mongo app already initialized')
  }

  app = new Realm.App({ id: process.env.VUE_APP_MONGO_CLIENT_ID })
}

export async function login(token) {
  if (!app) {
    init()
  }

  const credentials = Realm.Credentials.jwt(token)
  try {
    user = await app.logIn(credentials)
    client = app.currentUser.mongoClient('mongodb-atlas')
  } catch (error) {
    console.error('Mongodb login failed')
  }

  return user
}

export async function getClinicalLogs(userId, { specialty }) {
  const logs = await getClinicalLogCollection(specialty).find({ userId })
  return logs.map(fromMongoLog)
}

export async function getClinicalLogById(id, { specialty }) {
  const log = await getClinicalLogCollection(specialty).findOne({
    $or: [{ _id: ObjectId(id) }, { id: id }]
  })
  return fromMongoLog(log)
}

export async function getClinicalLogsByUserId(userIds, { specialty }) {
  const res = await axios.get(`/logs/${specialty}?userId=${userIds.join(',')}`)
  const logs = res.data
  return logs.map(fromMongoLog)
}

export async function insertClinicalLog(log, { specialty }) {
  const collection = getClinicalLogCollection(specialty)

  const { insertedId } = await collection.insertOne({
    ...log,
    createdAt: new Date()
  })

  return getClinicalLogById(insertedId, { specialty })
}

export async function insertManyClinicalLogs(logs, { specialty }) {
  const collection = getClinicalLogCollection(specialty)

  const createdAt = new Date()

  const { insertedIds } = await collection.insertMany(
    logs.map(log => ({ ...log, createdAt }))
  )

  const ids = insertedIds.map(id => ObjectId(id))

  return collection.find({ _id: { $in: ids } })
}

export async function updateClinicalLog(log, { specialty }) {
  const { id, _id, ...newlog } = log
  const collection = getClinicalLogCollection(specialty)

  await collection.updateOne(
    { _id: ObjectId(_id) },
    {
      $set: { ...newlog, updatedAt: new Date() }
    }
  )

  return getClinicalLogById(log._id, { specialty })
}

export function removeClinicalLogs(logs, { specialty }) {
  const ids = logs.map(log => ObjectId(log._id))
  return getClinicalLogCollection(specialty).deleteMany({ _id: { $in: ids } })
}

export async function getClinicalLogCount(specialty, userId) {
  if (!specialty || !userId) {
    return console.error(
      `Expected arguments specialty and userId, got ${specialty}, ${userId}`
    )
  }

  const collection = getClinicalLogCollection(specialty)
  const count = await getCount(
    { collection },
    { prop: 'userId', value: userId }
  )

  return count
}

export function getMongoUser() {
  return app.currentUser
}

export async function insertEntry(dbName, collectionName, data) {
  const collection = client.db(dbName).collection(collectionName)

  const createdAt = new Date()

  data = { ...data, createdAt }

  const newEntry = await collection.insertOne(data)
  return collection.findOne({ _id: newEntry.insertedId })
}

export async function insertMany(dbName, collectionName, data) {
  const collection = client.db(dbName).collection(collectionName)

  const createdAt = new Date()

  const { insertedIds } = await collection.insertMany(
    data.map(d => ({ ...d, createdAt }))
  )

  const ids = insertedIds.map(id => ObjectId(id))

  return collection.find({ _id: { $in: ids } })
}

export async function updateEntry(dbName, collectionName, data) {
  const { _id } = data
  const collection = getCollection(dbName, collectionName)

  if (!_id) {
    throw new Error('Object id not found.')
  }

  await collection.updateOne(
    { _id: ObjectId(_id) },
    {
      $set: { ...data, updatedAt: new Date() }
    }
  )

  return collection.findOne({ _id: ObjectId(_id) })
}

export async function deleteEntries(dbName, collectionName, data) {
  const collection = getCollection(dbName, collectionName)

  const ids = data.map(log => ObjectId(log._id))

  await collection.deleteMany({ _id: { $in: ids } })

  return data
}

export function getEntries(dbName, collectionName) {
  const collection = getCollection(dbName, collectionName)

  return collection.find()
}

export async function getUsersByIds(userIds) {
  const url = `/users?userId=${userIds.join(',')}`
  const cachedResponse = await getCachedResponse(url)

  if (cachedResponse) {
    return cachedResponse
  }

  const res = await axios.get(`/users/usernames?userId=${userIds.join(',')}`)
  const userNames = res.data

  cacheResponse(url, userNames, 60 * 60 * 24 * 5)

  return userNames
}

export async function getCount(
  { dbName, collectionName, collection },
  { prop, value }
) {
  const _collection = collection || client.db(dbName).collection(collectionName)
  const agg = await _collection.aggregate([
    {
      $match: {
        [prop]: {
          $eq: value
        }
      }
    },
    {
      $count: 'count'
    }
  ])

  let count = 0
  if (agg.length > 0) {
    count = agg[0].count
  }
  return count
}

function getCollection(dbName, collectionName) {
  if (!dbName) {
    throw new Error('Please provide the database name')
  }

  if (!collectionName) {
    throw new Error('Please provide the collection name')
  }

  return client.db(dbName).collection(collectionName)
}

const specialtyMap = {
  pediatric: 'Pediatrics',
  orl: 'OtoRhinoLaryngology'
}

function getClinicalLogCollection(specialty) {
  const mappedName = specialtyMap[specialty]
  const name = specialty.replace(/(^\w|-\w)/g, str =>
    str.replace(/-/, '').toUpperCase()
  )
  return client.db('ClinicalLogs').collection(mappedName || name)
}

function fromMongoLog(log) {
  if (log === null) {
    console.error('recieved null log')
    return
  }
  const _id = log._id.toString()
  return {
    ...log,
    id: log.id || _id,
    _id
  }
}
